/**
 * The workout outbox, run: WHEN to drain, and what to do after a sync.
 *
 * What to send, in which order, and what a failure means is `@repo/client`'s
 * pure logic (T21: `drain`, `classify`, the store). This file only schedules.
 *
 * ── WHY THE WORKOUT DOES NOT USE PERSISTED QUERY MUTATIONS ─────────────────
 * Do not "simplify" this into `useMutation` + the persister. A paused TanStack
 * mutation survives a restart only if its `mutationKey` has a
 * `setMutationDefaults` registration, because functions are not persisted;
 * the persisted cache is also disposable by design (D17) and busted by any
 * update that changes a shape it holds (`D190`). A set logged offline would
 * then be lost by an app update. The
 * outbox lives in Capacitor Preferences and replays in session order (D18–D23).
 *
 * ── DRAIN TRIGGERS ─────────────────────────────────────────────────────────
 * start · the network coming back · the app coming to the foreground · every
 * enqueue · the retry/discard of a quarantined item · the time the earliest
 * backed-off item falls due. Only one loop runs at a time: a trigger during a
 * loop asks for one more pass when it finishes, it does not start a second.
 * Offline, a trigger does nothing and the due-time timer is cancelled.
 *
 * Two of those triggers are a WAKE — the offline→online edge and the app
 * becoming active — and they clear the backoff first (`store.resetBackoff`).
 * A backoff computed while the network was down is up to 60 s of waiting for a
 * network that is now back; the due-time timer must NOT reset it, or a failure
 * that keeps failing would retry every second forever. A 429's `Retry-After`
 * survives a wake: the server asked for that delay.
 */
import type { QueryKey } from "@repo/client/data/core";
import {
  type DrainResult,
  drain,
  type OutboxEntry,
  type OutboxItem,
  type OutboxKind,
  type OutboxScope,
  type OutboxState,
  type OutboxStore,
  pendingCount,
  quarantined,
} from "@repo/client/offline";
import { createContext, useContext, useSyncExternalStore } from "react";

/** The online signal. TanStack's `onlineManager` fits this as it is. */
export interface OnlineSignal {
  isOnline(): boolean;
  subscribe(listener: (online: boolean) => void): () => void;
}

export interface Timers {
  clearTimeout(handle: unknown): void;
  setTimeout(callback: () => void, ms: number): unknown;
}

export interface OutboxRunnerOptions {
  /** Invalidate the given prefixes; the QueryClient in the app. */
  readonly invalidate: (keys: readonly QueryKey[]) => Promise<void> | void;
  /** Keys to invalidate after an item of this kind syncs. */
  readonly invalidatesFor: (kind: OutboxKind) => readonly QueryKey[];
  readonly network: OnlineSignal;
  readonly now?: () => Date;
  /** Foreground/background changes; `onAppActiveChange` in the app. */
  readonly onAppActive?: (handler: (active: boolean) => void) => () => void;
  /** The session is gone; the item stays pending until the next sign-in. */
  readonly onReauth?: () => void;
  /**
   * Told whenever {@link OutboxRunnerOptions.scope} would answer differently
   * (`accountScope.subscribe`). It is a re-render source for {@link useOutbox},
   * not a drain trigger: the sign-in path already triggers one once the
   * session flips, and the provider does after the scope has loaded.
   */
  readonly onScopeChange?: (listener: () => void) => () => void;
  /**
   * The server accepted this item, with the value `send` resolved to. Called
   * once per accepted item, in queue order, before the item is marked done.
   *
   * It is the ONLY place a write's answer is seen: the queue keeps nothing
   * after `markDone`, and the answer to `POST /me/sessions/:id/end` carries
   * the next session's changes, which no read re-derives (D116). A throw is
   * caught and warned — it must never reach `drain`, which would classify it
   * and requeue a write the server already took.
   */
  readonly onSent?: (item: OutboxItem, answer: unknown) => void;
  readonly random?: () => number;
  /**
   * Whose writes this runner may send and count: the account this device
   * holds (`account.ts`). Read at each use, never captured, because it moves.
   *
   * Omitted, the runner is unscoped — every item, which is the behaviour
   * before T34 and what the Node tests build. `{ accountId: null }` is the
   * other thing: no account is known, so NOTHING is sent (`@repo/client`).
   */
  readonly scope?: () => OutboxScope;
  readonly send: (item: OutboxItem) => Promise<unknown>;
  readonly store: OutboxStore;
  readonly timers?: Timers;
}

export interface OutboxRunner {
  /** The account this runner is scoped to; `null` when none or unscoped. */
  accountId(): string | null;
  discard(id: string): Promise<void>;
  enqueue(entry: OutboxEntry): Promise<OutboxItem>;
  /**
   * Several entries as ONE store write, then ONE drain. For writes a kill
   * must not split — the day's last set and its session's end (`T-196`).
   */
  enqueueMany(entries: readonly OutboxEntry[]): Promise<readonly OutboxItem[]>;
  /** Resolves when the loop that is running (if any) has finished. */
  idle(): Promise<void>;
  /** Let a held start go now (`D194`), then drain. */
  release(entityId: string): Promise<void>;
  retry(id: string): Promise<void>;
  /** Whether a scope was given at all — unscoped counts every item. */
  readonly scoped: boolean;
  /** Subscribe to triggers and drain once. Returns the stop. */
  start(): () => void;
  readonly store: OutboxStore;
  /** Re-render source for {@link useOutbox}: the account changing. */
  subscribeScope(listener: () => void): () => void;
  /** Ask for a drain. Never starts a second loop. */
  trigger(): void;
}

/** An unscoped runner's `subscribeScope`: the account never moves. */
const noScopeChanges = () => () => undefined;

const realTimers: Timers = {
  clearTimeout: (handle) =>
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
};

/** A timer longer than this is clamped (setTimeout overflows at 2^31 − 1). */
const MAX_TIMER_MS = 2_147_483_647;

export function createOutboxRunner(options: OutboxRunnerOptions): OutboxRunner {
  const { network, store } = options;
  const now = options.now ?? (() => new Date());
  const timers = options.timers ?? realTimers;
  let loop: Promise<void> | null = null;
  let running = false;
  let again = false;
  let timer: unknown = null;

  // Stamped here rather than by the caller, so no write can reach the queue
  // unowned by forgetting to: the workout screen never names an account. An
  // entry that names one itself is left alone.
  const owned = (entry: OutboxEntry): OutboxEntry => ({
    ...entry,
    accountId: entry.accountId ?? options.scope?.().accountId ?? null,
  });

  const clearTimer = () => {
    if (timer !== null) {
      timers.clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (result: DrainResult) => {
    clearTimer();
    if (result.reason === "reauth" || result.nextAttemptAt === null) {
      return;
    }
    const delay = Math.min(
      MAX_TIMER_MS,
      Math.max(0, result.nextAttemptAt.getTime() - now().getTime())
    );
    timer = timers.setTimeout(() => {
      timer = null;
      trigger();
    }, delay);
  };

  /** One pass of `drain`, then one invalidation for what it synced. */
  const pass = async (): Promise<DrainResult> => {
    // Collected per pass and invalidated once after it: a session of twenty
    // sets synced in one pass refetches its reads once, not twenty times.
    const touched = new Map<string, QueryKey>();
    const result = await drain({
      now,
      onReauth: () => options.onReauth?.(),
      ...(options.random === undefined ? {} : { random: options.random }),
      // Read per pass: the person may have signed in since the last one, and
      // another account's items are then not merely skipped but never
      // claimed, so they keep their attempts and their backoff (T28).
      ...(options.scope === undefined ? {} : { scope: options.scope() }),
      send: async (item) => {
        const answer = await options.send(item);
        try {
          options.onSent?.(item, answer);
        } catch (error) {
          // Never rethrow: `drain` would classify this as the send failing and
          // requeue a write the server has already accepted.
          console.warn("[data] outbox onSent failed", error);
        }
        for (const key of options.invalidatesFor(item.kind)) {
          touched.set(JSON.stringify(key), key);
        }
        return answer;
      },
      store,
    });
    if (touched.size > 0) {
      try {
        await options.invalidate([...touched.values()]);
      } catch (error) {
        console.warn("[data] outbox invalidation failed", error);
      }
    }
    return result;
  };

  /**
   * Passes until no trigger arrived during the last one.
   *
   * `running` is cleared in the SAME synchronous step that decides to stop.
   * Cleared any later — in a `.finally`, one microtask on — a trigger landing
   * in between would see a loop that has already made its last check, set
   * `again`, and be lost: an enqueued set that waits for the next trigger.
   */
  const runLoop = async (): Promise<void> => {
    for (;;) {
      again = false;
      let result: DrainResult;
      try {
        result = await pass();
      } catch (error) {
        // Storage refused to load or write. The queue on disk is untouched;
        // the next trigger tries again.
        running = false;
        console.warn("[data] outbox drain failed", error);
        return;
      }
      const online = network.isOnline();
      if (!(again && online && result.reason !== "reauth")) {
        running = false;
        if (online) {
          schedule(result);
        }
        return;
      }
    }
  };

  /**
   * The network came back, or the app did: clear the backoffs and drain.
   *
   * `resetBackoff` is awaited before the trigger, because a pass that starts
   * first sees the old `nextAttemptAt` and schedules a timer instead of
   * sending. A storage failure still drains what is already due.
   */
  function wake(): void {
    store.resetBackoff().then(trigger, (error: unknown) => {
      console.warn("[data] outbox backoff reset failed", error);
      trigger();
    });
  }

  function trigger(): void {
    if (!network.isOnline()) {
      return;
    }
    if (running) {
      again = true;
      return;
    }
    running = true;
    const current = runLoop();
    loop = current;
    current.finally(() => {
      if (loop === current) {
        loop = null;
      }
    });
  }

  return {
    accountId: () => options.scope?.().accountId ?? null,
    async discard(id) {
      await store.discardQuarantined(id);
      // A discarded item no longer holds its session: what queued behind it
      // may now go.
      trigger();
    },
    async enqueue(entry) {
      const item = await store.enqueue(owned(entry));
      trigger();
      return item;
    },
    async enqueueMany(entries) {
      const items = await store.enqueueMany(entries.map(owned));
      trigger();
      return items;
    },
    idle: async () => {
      while (loop !== null) {
        await loop;
        await Promise.resolve();
      }
    },
    async release(entityId) {
      await store.release(entityId);
      trigger();
    },
    async retry(id) {
      await store.retryQuarantined(id);
      trigger();
    },
    scoped: options.scope !== undefined,
    start() {
      // `onlineManager` may re-emit `true` while already online; only the
      // offline→online EDGE is a reconnect worth clearing a backoff for.
      let wasOnline = network.isOnline();
      const offNetwork = network.subscribe((online) => {
        const reconnected = online && !wasOnline;
        wasOnline = online;
        if (!online) {
          clearTimer();
          return;
        }
        if (reconnected) {
          wake();
        } else {
          trigger();
        }
      });
      const offActive = options.onAppActive?.((active) => {
        if (active) {
          wake();
        }
      });
      store.load().catch((error: unknown) => {
        console.warn("[data] outbox load failed", error);
      });
      trigger();
      return () => {
        offNetwork();
        offActive?.();
        clearTimer();
      };
    },
    store,
    subscribeScope: options.onScopeChange ?? noScopeChanges,
    trigger,
  };
}

// ── React ────────────────────────────────────────────────────────────────────

export interface OutboxView {
  discard(id: string): Promise<void>;
  enqueue(entry: OutboxEntry): Promise<OutboxItem>;
  /** Unsynced items not quarantined: waiting, backing off, or on the wire. */
  readonly pending: number;
  /** Items the server refused; they hold their session until retried or discarded. */
  readonly quarantined: readonly OutboxItem[];
  retry(id: string): Promise<void>;
  readonly state: OutboxState;
}

export const OutboxContext = createContext<OutboxRunner | null>(null);

/**
 * The outbox for T53's UI. Must render inside `DataProvider`.
 *
 * `pending` and `quarantined` are THIS account's: the sign-out confirmation
 * counts what this person would lose, not what another account left on the
 * device (`use-push.ts`'s `useSignOutWithPush`). Two stores are subscribed for
 * that — the queue and the account — because either moving changes the answer.
 */
export function useOutbox(): OutboxView {
  const runner = useContext(OutboxContext);
  if (runner === null) {
    throw new Error("useOutbox must be used inside DataProvider");
  }
  const state = useSyncExternalStore(
    runner.store.subscribe,
    runner.store.getState,
    runner.store.getState
  );
  const accountId = useSyncExternalStore(
    runner.subscribeScope,
    runner.accountId,
    runner.accountId
  );
  // Built during render, never as a store snapshot: a fresh object as a
  // snapshot re-renders forever. Undefined = unscoped, which is every item.
  const scope = runner.scoped ? { accountId } : undefined;
  return {
    discard: runner.discard,
    enqueue: runner.enqueue,
    pending: pendingCount(state, scope),
    quarantined: quarantined(state, scope),
    retry: runner.retry,
    state,
  };
}
